import * as braze from '@braze/web-sdk';
import type { UserInfo } from '@intigral/domain/lib/core/entities/UserInfo';
import { BRAZE } from '../../config/analytics-events';
import { getBrazeData, getGuid } from '../../helpers/common';

type BrazeConfig = {
    apiKey?: string;
    enableBraze?: boolean;
    configParams?: Record<string, unknown>;
};

type BrazeAttributeValue = string | number | boolean | string[] | Date;

let sdkInitialized = false;
let identifiedUserId: string | null = null;
const initListeners = new Set<() => void>();

const SDK_OPTIONS = {
    allowUserSuppliedJavascript: true,
    enableLogging: false,
    enableHtmlInAppMessages: true,
    doNotLoadFontAwesome: true,
    disablePushTokenMaintenance: true,
    manageServiceWorkerExternally: false,
    minimumIntervalBetweenTriggerActionsInSeconds: 10,
};

export const hasValidUserData = (userInfo?: UserInfo | null): userInfo is UserInfo =>
    !!(userInfo && (userInfo.uid || userInfo.accountId || userInfo.username));

/** Prefer uid for new users; fall back to accountId when uid is not yet available. */
export const getBrazeExternalId = (userInfo: UserInfo): string | null => {
    const id = userInfo.uid || userInfo.accountId;
    return id != null ? String(id) : null;
};

export const isBrazeInitialized = (): boolean => sdkInitialized;

export const subscribeToBrazeInit = (listener: () => void): (() => void) => {
    initListeners.add(listener);
    return () => {
        initListeners.delete(listener);
    };
};

const getBrazeConfig = (): BrazeConfig => getBrazeData() as BrazeConfig;

const isBrazeEnabled = (config: BrazeConfig): boolean => !!(config.apiKey && config.enableBraze);

const getSubscriptionPackages = (): string => {
    try {
        return window.decodeURIComponent(getGuid()).split('|').join(', ');
    } catch {
        return '';
    }
};

const applyCustomAttributes = (attributes: Record<string, unknown>): void => {
    Object.entries(attributes).forEach(([key, value]) => {
        braze.getUser().setCustomUserAttribute(key, value as BrazeAttributeValue, true);
    });
};

const buildUserAttributes = (userInfo: UserInfo): Record<string, unknown> => ({
    [BRAZE.SUPER_PROPERTIES.USER_ID]: getBrazeExternalId(userInfo) ?? '',
    [BRAZE.SUPER_PROPERTIES.OPERATOR]: userInfo.operator ?? '',
    [BRAZE.SUPER_PROPERTIES.SUBSCRIPTION_START_DATE]: userInfo.firstLoginDate ? new Date(userInfo.firstLoginDate) : '',
    [BRAZE.SUPER_PROPERTIES.STATUS]: userInfo.userStatus ?? '',
    [BRAZE.SUPER_PROPERTIES.SUBSCRIPTION_PACKAGES]: getSubscriptionPackages(),
});

const identifyUser = (userInfo: UserInfo): boolean => {
    if (!sdkInitialized) {
        return false;
    }

    const externalId = getBrazeExternalId(userInfo);
    if (!externalId) {
        return false;
    }

    if (identifiedUserId !== externalId) {
        braze.changeUser(externalId);
        identifiedUserId = externalId;
        applyCustomAttributes(buildUserAttributes(userInfo));
    }

    return true;
};

const initializeSdk = (config: BrazeConfig): boolean => {
    const initialized = braze.initialize(config.apiKey!, {
        ...config.configParams,
        ...SDK_OPTIONS,
    } as any);

    if (!initialized) {
        return false;
    }

    sdkInitialized = true;
    braze.openSession();
    // deferred: init runs during AppWrapper's render
    queueMicrotask(() => initListeners.forEach((listener) => listener()));
    return true;
};

/**
 * Initializes the Braze SDK once and identifies the user.
 * Subsequent calls only re-identify when the external id changes (e.g. uid becomes available).
 */
export const initializeBraze = (userInfo: UserInfo): boolean => {
    if (!hasValidUserData(userInfo)) {
        return false;
    }

    const config = getBrazeConfig();
    if (!isBrazeEnabled(config)) {
        return false;
    }

    if (sdkInitialized) {
        return identifyUser(userInfo);
    }

    if (!initializeSdk(config)) {
        return false;
    }

    return identifyUser(userInfo);
};

export const logBrazeCustomEvent = (eventName: string, eventProperties?: Record<string, unknown>): void => {
    if (!sdkInitialized) {
        return;
    }

    braze.logCustomEvent(eventName, eventProperties);
};

export const flushBrazeData = (): Promise<void> =>
    new Promise((resolve) => {
        if (!sdkInitialized) {
            resolve();
            return;
        }
        braze.requestImmediateDataFlush(() => resolve());
    });

/** Clears tracked identity so the next session re-identifies correctly after logout. */
export const resetBrazeIdentity = (): void => {
    identifiedUserId = null;
};
